// src/context/AuthContext.js

// 1. IMPORTS : on récupère les outils React nécessaires
import React, { createContext, useState, useContext, useEffect } from 'react';
// createContext → crée un espace de stockage global (le "contexte")
// useState → pour gérer des données qui changent (ex: user connecté)
// useContext → pour lire le contexte dans un composant
// useEffect → pour exécuter du code au premier affichage

// 2. SERVICE : les fonctions qui parlent au serveur (login, logout, getCurrentUser)
import authService from '../services/auth/authService';

// 3. CRÉATION DU CONTEXTE : c'est la "boîte" vide qui contiendra les données d'auth
const AuthContext = createContext();

// 4. HOOK PERSONNALISÉ : "useAuth" → un petit raccourci magique
//    Pourquoi c'est fort ? Parce que n'importe quel composant peut faire :
//    const { user, login } = useAuth();  → sans se soucier du contexte.
export const useAuth = () => useContext(AuthContext);

// 5. PROVIDER : le composant qui "fournit" les données à toute l'application
//    Il enveloppe les autres composants (les enfants) et partage avec eux user, login, etc.
export const AuthProvider = ({ children }) => {
  // --- ÉTATS locaux ---
  const [user, setUser] = useState(null);
  // user : l'utilisateur connecté (ou null si déconnecté)
  const [loading, setLoading] = useState(true);
  // loading : vrai tant qu'on vérifie si l'utilisateur est déjà connecté

  // --- AU DÉMARRAGE : on regarde si un utilisateur était déjà connecté ---
  useEffect(() => {
    const currentUser = authService.getCurrentUser(); // ex: lit le token dans localStorage
    setUser(currentUser);      // on met à jour user
    setLoading(false);         // le chargement est terminé
  }, []); // le tableau vide = exécuté une seule fois

  // --- FONCTION DE CONNEXION ---
  const login = async (email, password) => {
    const result = await authService.login(email, password); // appel au serveur
    if (result.success) setUser(result.user); // si ok, on sauvegarde l'utilisateur
    return result; // on renvoie le résultat pour que le composant appelant sache si ça a marché
  };

  // --- FONCTION DE DÉCONNEXION ---
  const logout = async () => {
    await authService.logout(); // on dit au serveur et on nettoie le token
    setUser(null);              // on supprime l'utilisateur de l'état
  };

  // --- LE RENDU : le Provider (vrai fournisseur React) ---
  //    Toutes les valeurs entre accolades ({ user, login, logout, loading })
  //    seront accessibles à tous les composants enfants.
  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};